import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

/**
 * StockfishEval - A vertical evaluation bar that visualizes the advantage in a chess position
 * 
 * @param {Object} props Component props
 * @param {number} props.evaluation The evaluation value (-Infinity to +Infinity, positive favors white)
 * @param {string} props.orientation Board orientation ('white' or 'black')
 * @param {number} props.height Height of the entire component (default: 100%)
 * @param {number} props.width Width of the bar in pixels (default: 25)
 * @param {number} props.boardSize Size of the chessboard to align with
 * @param {boolean} props.isHidden Whether the evaluation display is hidden
 * @param {boolean} props.isAnalyzing Track if analysis is in progress
 * @param {function} props.onToggleVisibility Callback to toggle the visibility state
 */
const StockfishEval = ({
    evaluation: initialEvaluation = 0,
    orientation = 'white',
    height = '100%',
    width = 30,
    boardSize = 500,
    isHidden = false,
    isAnalyzing = false,
    onToggleVisibility = () => { }
}) => {
    // Use local state to track the evaluation so we can modify it for testing
    const [evaluation, setEvaluation] = useState(initialEvaluation);
    // Track if the evaluation is a mate score
    const [mateScore, setMateScore] = useState(null);

    // Update local evaluation when prop changes (when connected to engine)
    useEffect(() => {
        // Check if this is a mate score
        if (typeof initialEvaluation === 'string' && initialEvaluation.startsWith('M')) {
            const mateText = initialEvaluation.substring(1); // Remove the 'M'
            const mateNumber = parseInt(mateText);
            if (!isNaN(mateNumber)) {
                setMateScore(mateNumber);
                // Use a very high/low value for the bar
                setEvaluation(mateNumber > 0 ? 100 : -100);

            } else {
                setMateScore(null);
                setEvaluation(initialEvaluation);
            }
        } else if (typeof initialEvaluation === 'number') {
            setMateScore(null);
            setEvaluation(initialEvaluation);
        } else if (initialEvaluation !== undefined && initialEvaluation !== null) {
            // Try to parse as a number if it's a string but not a mate score
            const numericEval = parseFloat(initialEvaluation);
            if (!isNaN(numericEval)) {
                setMateScore(null);
                setEvaluation(numericEval);

            }
        }
    }, [initialEvaluation]);

    // NEW: Listen for direct evaluation updates from Stockfish3
    useEffect(() => {
        const handleEvaluationUpdate = (event) => {
            const { evaluation, fen, timestamp } = event.detail;
            
            adjustEvaluation(evaluation);
        };

        window.addEventListener('stockfish-evaluation-update', handleEvaluationUpdate);
        
        return () => {
            window.removeEventListener('stockfish-evaluation-update', handleEvaluationUpdate);
        };
    }, []);

    // Calculate the proportions for the white and black sections of the bar
    const [proportions, setProportions] = useState({ white: 50, black: 50 });

    // For testing: Adjust evaluation manually
    const [showTestControls, setShowTestControls] = useState(false);
    const [testEvalInput, setTestEvalInput] = useState('0.0');

    // Advantage tiers - easily adjustable for fine-tuning
    const ADVANTAGE_TIERS = {
        EVEN: 0.3,       // Absolute evaluation ≤ 0.3
        BETTER: 1.0,     // Absolute evaluation ≤ 1.0 (but > 0.3)
        WINNING: 3.0,    // Absolute evaluation ≤ 3.0 (but > 1.0)
        CRUSHING: 6.0,   // Absolute evaluation ≤ 6.0 (but > 3.0)
        RESIGN: 10.0 // Absolute evaluation ≤ 10.0 (but > 6.0)
    };

    // Test function to adjust evaluation
    const adjustEvaluation = (newEval) => {
        // Check if this is a mate score
        if (typeof newEval === 'string' && newEval.toLowerCase().startsWith('m')) {
            const mateNumber = parseInt(newEval.substring(1));
            if (!isNaN(mateNumber)) {
                setMateScore(mateNumber);
                // Set an extremely high value for visualization
                setEvaluation(mateNumber > 0 ? 100 : -100);
                return;
            }
        }

        // Parse the input as a float if not a mate score
        const numericEval = parseFloat(newEval);
        if (!isNaN(numericEval)) {
            setMateScore(null);
            setEvaluation(numericEval);
        }
    };

    // Convert numeric evaluation to a visual representation
    useEffect(() => {
        // Determine the dominant percentage (which side gets more space)
        // This is based solely on the evaluation
        let dominantPercentage = 50; // Start even

        // Calculate dominant percentage based on evaluation
        if (mateScore !== null) {
            // If it's a mate score, give the winning side significant dominance
            dominantPercentage = 80; // 80% for winning side, 20% for losing side
        } else if (evaluation !== 0) {
            // Calculate dominance based on evaluation
            const cappedEval = Math.max(-10, Math.min(10, evaluation));
            const absoluteEval = Math.abs(cappedEval);

            // Scale dominance based on evaluation size
            if (absoluteEval <= 5) {
                // Linear scaling from 50% to 80% for evals 0-5
                dominantPercentage = 50 + (absoluteEval * 6);
            } else {
                // For larger evals, gradually approach 95%
                const remainingSpace = 20 - ((absoluteEval - 5) / 5) * 15;
                dominantPercentage = 100 - remainingSpace;
            }
        }

        // Determine which colors get what percentages
        // White is dominant if evaluation is positive, Black if negative
        let whitePercentage, blackPercentage;

        // Assign percentages based on which side is winning
        if (evaluation > 0 || (mateScore !== null && mateScore > 0)) {
            // White is winning - white gets the dominant percentage
            whitePercentage = dominantPercentage;
            blackPercentage = 100 - dominantPercentage;
        } else if (evaluation < 0 || (mateScore !== null && mateScore < 0)) {
            // Black is winning - black gets the dominant percentage
            blackPercentage = dominantPercentage;
            whitePercentage = 100 - dominantPercentage;
        } else {
            // Even position
            whitePercentage = 50;
            blackPercentage = 50;
        }

        // Apply orientation-specific positioning
        // For WHITE orientation: white at bottom, black at top 
        // For BLACK orientation: black at bottom, white at top
        // This affects how the percentages are applied visually
        setProportions({
            white: whitePercentage,
            black: blackPercentage
        });
    }, [evaluation, orientation, mateScore]);

    // Format the evaluation display value
    const formatEvaluation = () => {
        // Handle mate scores
        if (mateScore !== null) {
            // Show as "M5" for mate in 5 for White, or "M-5" for mate in 5 for Black
            return mateScore > 0 ? `M${mateScore}` : `M${mateScore}`;
        }

        if (evaluation === Infinity) return '♔ Mate';
        if (evaluation === -Infinity) return '♚ Mate';

        // Show the evaluation with at most 1 decimal place
        const absEval = Math.abs(evaluation);
        const displayEval = absEval >= 10 ? Math.floor(absEval) : absEval.toFixed(1);

        // Add + sign for positive values
        return evaluation > 0 ? `+${displayEval}` : evaluation < 0 ? `-${displayEval}` : '0.0';
    };

    // Determine the advantage tier based on evaluation
    const getAdvantageTier = () => {
        // Handle mate scores specially
        if (mateScore !== null) {
            return `MATE ${Math.abs(mateScore)}`;
        }

        const absEval = Math.abs(evaluation);

        if (absEval <= ADVANTAGE_TIERS.EVEN) return "EVEN";
        if (absEval <= ADVANTAGE_TIERS.BETTER) return "BETTER";
        if (absEval <= ADVANTAGE_TIERS.WINNING) return "WINNING";
        if (absEval <= ADVANTAGE_TIERS.CRUSHING) return "CRUSHING";
        if (absEval <= ADVANTAGE_TIERS.PLS_RESIGN) return "RESIGN";
        return "RESIGN"; // Fallback for values > 10
    };

    // Determine which side has the advantage
    const isEven = Math.abs(evaluation) <= ADVANTAGE_TIERS.EVEN;

    // FIXED: Correct perspective logic for current player
    // If we're viewing from white's perspective: positive eval = white better, negative = white worse
    // If we're viewing from black's perspective: positive eval = black worse, negative = black better
    const isCurrentPlayerWinning = orientation === 'white' ? evaluation > 0 : evaluation < 0;

    // FIXED: Show proper advantage text from current player's perspective
    const getAdvantageText = () => {
        if (isEven) return "EVEN";
        
        const tier = getAdvantageTier();
        if (tier.startsWith("MATE")) return tier;
        
        // For non-mate positions, show perspective-corrected text
        if (isCurrentPlayerWinning) {
            // Current player is winning
            switch (tier) {
                case "BETTER": return "BETTER";
                case "WINNING": return "WINNING";
                case "CRUSHING": return "CRUSHING";
                case "RESIGN": return "RESIGN";
                default: return tier;
            }
        } else {
            // Current player is losing
            switch (tier) {
                case "BETTER": return "WORSE";
                case "WINNING": return "LOSING";
                case "CRUSHING": return "CRUSHED";
                case "RESIGN": return "RESIGN";
                default: return tier;
            }
        }
    };

    // Show W for winning, L for losing from current player's perspective
    const leadingSide = isEven ? '' : (isCurrentPlayerWinning ? 'W' : 'L');

    // Is the evaluation good for the current perspective?
    const isGoodForPov = isCurrentPlayerWinning;

    // Colors based on perspective and analysis state
    const getTextColor = () => {
        // Check if we're still analyzing OR if analysis hasn't reached target depth (15)
        const isStillAnalyzing = isAnalyzing || (window.currentAnalysisCache && window.currentAnalysisCache.depth < 15);
        
        if (isStillAnalyzing) {
            return '#888'; // Gray while analyzing or depth < 15
        }
        
        if (isEven) {
            return 'white'; // White for even positions when analysis is complete
        }
        
        return isGoodForPov ? '#4CAF50' : '#f44336'; // Green for good, red for bad
    };

    const advantageColor = getTextColor();

    // Current advantage tier text with perspective correction
    const advantageTierText = getAdvantageText();

    // Determine if center line should be visible
    // Hide center line when it would be covered by the white part of the bar
    const showCenterLine = orientation === 'white'
        ? proportions.black > 50 // White at bottom, black section should be >50% to show line
        : proportions.white > 50; // Black at bottom, white section should be >50% to show line

    // Calculate heights - adjust to match actual components
    const evalBarHeight = boardSize + 8; // Match exactly the chessboard height
    const bottomSectionHeight = 140; // Match Stockfish component height

    // Toggle test controls visibility
    const toggleTestControls = () => {
        setShowTestControls(!showTestControls);
    };

    // Function to handle quick preset evaluations
    const setPresetEval = (presetValue) => {
        setTestEvalInput(presetValue.toString());
        adjustEvaluation(presetValue);
    };

    // CHANGE 2: Calculate evaluation marker position to ensure it stays within bounds
    // Modified to position mate indicators at the very edges of the bar
    const getMarkerPosition = () => {
        // Calculate marker position based on evaluation
        // Marker should be at the border between white and black sections

        // For even positions, marker is at 50%
        if (isEven) return orientation === 'white' ? '50%' : '50%';

        // For all other positions, calculate based on evaluation
        let borderPosition;

        // Different calculations based on orientation
        if (orientation === 'white') {
            // WHITE ORIENTATION: white at bottom (100%), black at top (0%)
            // Marker should be at the top of white section (or equivalently bottom of black section)
            borderPosition = 100 - proportions.white;
        } else {
            // BLACK ORIENTATION: black at bottom (100%), white at top (0%)
            // Marker should be at the bottom of white section (or equivalently top of black section)
            borderPosition = proportions.white;
        }

        // Ensure the marker stays within visible range
        return `${Math.max(5, Math.min(95, borderPosition))}%`;
    };

    // Determine marker color based on current perspective (POV) and analysis state
    const getMarkerColor = () => {
        // Check if we're still analyzing OR if analysis hasn't reached target depth (15)
        const isStillAnalyzing = isAnalyzing || (window.currentAnalysisCache && window.currentAnalysisCache.depth < 15);
        
        if (isStillAnalyzing) {
            return '#888'; // Gray while analyzing or depth < 15
        }
        
        if (isEven) {
            return 'white'; // White for even positions when analysis is complete
        }

        // For white orientation: green if white winning, red if black winning
        // For black orientation: green if black winning, red if white winning
        const isGoodForPov = orientation === 'white' ? evaluation > 0 : evaluation < 0;

        return isGoodForPov ? '#4CAF50' : '#f44336'; // Green for good, red for bad
    };

    return (
        <div
            className="stockfish-eval-container"
            style={{
                display: 'flex',
                flexDirection: 'column',
                width: `${width}px`,
                height: '100%',
                overflow: 'visible',
                backgroundColor: 'transparent',
                position: 'relative',
            }}
        >
            {/* Evaluation bar section */}
            <div
                className="eval-bar-section"
                style={{
                    height: `${evalBarHeight}px`,
                    width: '100%',
                    border: '1px solid #333',
                    borderRadius: '5px',
                    overflow: 'hidden',
                    position: 'relative',
                    backgroundColor: '#1a2635',
                }}
            // onDoubleClick={toggleTestControls}
            >
                {isHidden ? (
                    // When hidden, just show a solid dark bar
                    <div
                        className="eval-bar-hidden"
                        style={{
                            height: '100%',
                            backgroundColor: '#1a2635',
                            width: '100%',
                        }}
                    />
                ) : (
                    // Normal evaluation display when not hidden
                    <>
                        {orientation === 'white' ? (
                            // WHITE ORIENTATION: white at bottom, black at top
                            <>
                                <div
                                    className="eval-bar-black"
                                    style={{
                                        height: `${proportions.black}%`,
                                        backgroundColor: '#333',
                                        width: '100%',
                                        transition: 'height 0.3s ease',
                                        position: 'absolute',
                                        top: 0
                                    }}
                                />
                                <div
                                    className="eval-bar-white"
                                    style={{
                                        height: `${proportions.white}%`,
                                        backgroundColor: '#eee',
                                        width: '100%',
                                        transition: 'height 0.3s ease',
                                        position: 'absolute',
                                        bottom: 0
                                    }}
                                />
                            </>
                        ) : (
                            // BLACK ORIENTATION: black at bottom, white at top
                            <>
                                <div
                                    className="eval-bar-white"
                                    style={{
                                        height: `${proportions.white}%`,
                                        backgroundColor: '#eee',
                                        width: '100%',
                                        transition: 'height 0.3s ease',
                                        position: 'absolute',
                                        top: 0
                                    }}
                                />
                                <div
                                    className="eval-bar-black"
                                    style={{
                                        height: `${proportions.black}%`,
                                        backgroundColor: '#333',
                                        width: '100%',
                                        transition: 'height 0.3s ease',
                                        position: 'absolute',
                                        bottom: 0
                                    }}
                                />
                            </>
                        )}

                        {/* Center line - only visible when not covered by white section */}
                        {showCenterLine && (
                            <div
                                style={{
                                    position: 'absolute',
                                    left: 0,
                                    top: '50%',
                                    width: '100%',
                                    height: '1px',
                                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                    zIndex: 1
                                }}
                            />
                        )}

                        {/* Numeric evaluation display at the border between white and black */}
                        <div
                            style={{
                                position: 'absolute',
                                left: '50%',
                                top: getMarkerPosition(),
                                transform: 'translate(-50%, -50%)',
                                backgroundColor: '#222',
                                color: getMarkerColor(),
                                padding: '2px 6px',
                                borderRadius: '3px',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                border: '1px solid #444',
                                zIndex: 5,
                                // Extend slightly outside the container
                                boxShadow: '0 0 3px rgba(0,0,0,0.5)',
                                whiteSpace: 'nowrap',
                                minWidth: '28px',
                                textAlign: 'center',
                            }}
                        >
                            {formatEvaluation()}
                        </div>
                    </>
                )}
            </div>

            {/* Spacer that will adapt to match layout */}
            <div style={{ height: '10px' }} />

            {/* Bottom evaluation display or SHOW EVAL button when hidden */}
            <div
                className="eval-display"
                style={{
                    width: '100%',
                    height: `${bottomSectionHeight}px`,
                    border: '1px solid #333',
                    borderRadius: '5px',
                    backgroundColor: '#1a2635',
                    color: 'white',
                    fontSize: '12px',
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '4px 0',
                    cursor: isHidden ? 'pointer' : 'default',
                }}
                onClick={isHidden ? onToggleVisibility : undefined}
            >
                {isHidden ? (
                    // When hidden, show SHOW EVAL vertically
                    <div style={{
                        writingMode: 'vertical-rl',
                        textOrientation: 'upright',
                        fontSize: '12px',
                        letterSpacing: '-2px',
                        fontWeight: 'bold',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#f44336',
                    }}>
                        SHOW EVAL
                    </div>
                ) : isEven ? (
                    <div style={{
                        writingMode: 'vertical-rl',
                        textOrientation: 'upright',
                        fontSize: '14px',
                        letterSpacing: '-1px',
                        fontWeight: 'bold',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                        EVEN
                    </div>
                ) : (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}>
                        <div style={{
                            position: 'relative',
                            top: '-5px',
                            left: '0',
                            backgroundColor: isGoodForPov ? '#4CAF50' : '#f44336',
                            color: 'white',
                            borderRadius: '5px',
                            padding: '3px 8px',

                            fontSize: '14px',
                            fontWeight: 'bold',
                            minWidth: '20px',
                            textAlign: 'center',
                        }}>
                            {leadingSide}
                        </div>
                        <div style={{
                            writingMode: 'vertical-rl',
                            textOrientation: 'upright',
                            fontSize: '12px',
                            letterSpacing: '-3px',
                            fontWeight: 'bold',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: advantageColor,
                        }}>
                            {advantageTierText}
                        </div>
                    </div>
                )}
            </div>

            {/* Test Controls - Double click evaluation bar to show/hide */}
            {showTestControls && (
                <div
                    style={{
                        position: 'absolute',
                        top: `100px`,
                        left: `-135px`,
                        width: '300px',
                        padding: '10px',
                        backgroundColor: 'rgba(0, 0, 0, 0.9)',
                        border: '1px solid #555',
                        borderRadius: '5px',
                        zIndex: 1000,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '5px'
                    }}
                >
                    <div style={{ fontSize: '12px', color: '#fff', textAlign: 'center', marginBottom: '5px' }}>
                        Test Controls (Double-click eval bar to hide)
                    </div>
                    <div style={{ display: 'flex', gap: '5px', alignItems: 'center', marginBottom: '5px' }}>
                        <input
                            type="text"
                            value={testEvalInput}
                            onChange={(e) => setTestEvalInput(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    adjustEvaluation(testEvalInput);
                                }
                            }}
                            style={{
                                width: '70px',
                                padding: '5px',
                                borderRadius: '3px',
                                border: '1px solid #555',
                                backgroundColor: '#222',
                                color: '#fff'
                            }}
                        />
                        <button
                            onClick={() => adjustEvaluation(testEvalInput)}
                            style={{
                                padding: '5px 10px',
                                borderRadius: '3px',
                                border: '1px solid #4CAF50',
                                backgroundColor: '#2d4a22',
                                color: '#fff',
                                cursor: 'pointer'
                            }}
                        >
                            Set Eval
                        </button>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', justifyContent: 'center' }}>
                        <button onClick={() => setPresetEval(-5)} style={{ padding: '3px 6px', backgroundColor: '#f44336', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '11px' }}>-5.0</button>
                        <button onClick={() => setPresetEval(-3)} style={{ padding: '3px 6px', backgroundColor: '#ff7043', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '11px' }}>-3.0</button>
                        <button onClick={() => setPresetEval(-1)} style={{ padding: '3px 6px', backgroundColor: '#ffb74d', color: '#000', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '11px' }}>-1.0</button>
                        <button onClick={() => setPresetEval(-0.5)} style={{ padding: '3px 6px', backgroundColor: '#ffe082', color: '#000', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '11px' }}>-0.5</button>
                        <button onClick={() => setPresetEval(0)} style={{ padding: '3px 6px', backgroundColor: '#aaa', color: '#000', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '11px' }}>0.0</button>
                        <button onClick={() => setPresetEval(0.5)} style={{ padding: '3px 6px', backgroundColor: '#a5d6a7', color: '#000', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '11px' }}>+0.5</button>
                        <button onClick={() => setPresetEval(1)} style={{ padding: '3px 6px', backgroundColor: '#81c784', color: '#000', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '11px' }}>+1.0</button>
                        <button onClick={() => setPresetEval(3)} style={{ padding: '3px 6px', backgroundColor: '#4caf50', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '11px' }}>+3.0</button>
                        <button onClick={() => setPresetEval(5)} style={{ padding: '3px 6px', backgroundColor: '#2e7d32', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '11px' }}>+5.0</button>
                        <button onClick={() => setPresetEval('M1')} style={{ padding: '3px 6px', backgroundColor: '#2e7d32', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '11px' }}>M1</button>
                        <button onClick={() => setPresetEval('M-1')} style={{ padding: '3px 6px', backgroundColor: '#f44336', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '11px' }}>M1</button>
                    </div>
                </div>
            )}
        </div>
    );
};

StockfishEval.propTypes = {
    evaluation: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string // For mate scores like "M1", "M2", etc.
    ]),
    orientation: PropTypes.oneOf(['white', 'black']),
    height: PropTypes.string,
    width: PropTypes.number,
    boardSize: PropTypes.number,
    isHidden: PropTypes.bool,
    isAnalyzing: PropTypes.bool,
    onToggleVisibility: PropTypes.func
};

export default StockfishEval; 